import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  StringParam,
  withDefault,
  useQueryParams,
  NumberParam,
} from 'use-query-params';
import { debounce } from 'throttle-debounce';
import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import { SortingPill, I18nText } from '@welldev-group/react-comp-lib';
import Button from '@welldev-group/rcl-button';
import routes from '../../../../../../shared/routes';
import ConfirmationModal from '../../../../../../shared/components/ConfirmationModal';
import withQueryParamsProvider from '../../../../../../shared/components/withQueryParamsProvider';
import httpClient from '../../../../../../shared/libraries/httpClient';
import PaginationWithTranslation from '../../../../../../shared/components/Pagination/PaginationWithTranslation';
import WorkspaceTable from './WorkspaceTable';
import WorkSpaceModal from '../../../../Workspace/WorkSpaceModal';

const WorkspaceList = ({ id, status, userAffiliateId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [affiliates] = useState([]);
  const [superAdmin, setSuperAdmin] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceIdentifier, setWorkspaceIdentifier] = useState('');
  const [
    isConfirmationModalOfCreatingWorkspaceOpen,
    setIsConfirmationModalOfCreatingWorkspaceOpen,
  ] = useState(false);
  const [
    isConfirmationModalOfUpdatingWorkspaceOpen,
    setIsConfirmationModalOfUpdatingWorkspaceOpen,
  ] = useState(false);
  const [query, setQuery] = useQueryParams({
    tab: withDefault(StringParam, 'users'),
    q: withDefault(StringParam, ''),
    state: withDefault(StringParam, status),
    page: NumberParam,
    sortColumn: StringParam,
    sortDirection: StringParam,
  });
  const ACTIVE_STATUS = 'active';
  const isStateActive = status === ACTIVE_STATUS;

  const { tab, q, page, sortColumn, sortDirection } = query;

  const [localSearchQuery, setLocalSearchQuery] = useState(q);

  const setInitialParams = (data, searchQuery, state) => {
    // We only set the params onces when there is nothing in the url
    if (!query.page) {
      setQuery(
        {
          q: searchQuery,
          page: data.pagination.currentPage,
          sortColumn: data.sort.column,
          sortDirection: data.sort.direction,
          state,
        },
        // use replaceIn as we don't want another navigation to be added to the browser history
        'replaceIn',
      );
    }
  };

  const setSearchQueryInParams = React.useRef(
    debounce(500, value => setQuery({ q: value, page: 1 })),
  ).current;

  const setSearchQuery = value => {
    setIsLoading(true);
    setLocalSearchQuery(value);
    setSearchQueryInParams(value);
  };

  const fetchWorkspaces = async (
    searchQuery,
    newPage,
    column,
    direction,
    newState,
  ) => {
    const { data } = await httpClient.get(
      routes.workspace.fetch({
        affiliate_id: id,
        state: status,
        q: searchQuery,
        page: newPage,
        column,
        direction,
      }),
    );
    setWorkspaces(data.workspaces);
    setInitialParams(data, searchQuery, newState);
    setSuperAdmin(data.superAdmin);
    setTotalPages(data.pagination.totalPages);
    setIsLoading(false);
  };

  const toggleDirection = () => {
    if (sortDirection === 'up') {
      setQuery({ sortDirection: 'down' });
    } else {
      setQuery({ sortDirection: 'up' });
    }
  };

  const setSortWorkspace = chosenColumn => {
    if (sortColumn === chosenColumn) {
      toggleDirection();
    } else {
      setQuery({ sortColumn: chosenColumn, sortDirection: 'up' });
    }
  };

  const createWorkspace = async () => {
    const params = {
      workspace: {
        name: workspaceName,
        identifier: workspaceIdentifier,
      },
    };
    const { data } = await httpClient.post(
      routes.workspace.create({ affiliate_id: id }),
      params,
    );
    return data;
  };

  const openConfirmationModalOfCreatingWorkspace = () => {
    setIsConfirmationModalOfCreatingWorkspaceOpen(true);
  };

  const deleteWorkspace = async workspaceId => {
    httpClient
      .delete(routes.workspace.delete({ id: workspaceId, affiliate_id: id }))
      .then(response => {
        fetchWorkspaces(q, page, sortColumn, sortDirection, status);
        return response;
      });
  };

  useEffect(() => {
    setQuery({ tab: 'workspaces', state: status, page: 1 }, 'replaceIn');
  }, [status]);

  useEffect(() => {
    setQuery({ tab: 'workspaces', state: status }, 'replaceIn');
    fetchWorkspaces(q, page, sortColumn, sortDirection, status);
  }, [q, tab, page, sortColumn, sortDirection]);

  return (
    <div>
      <div className="col-grid col-4 col-bleed">
        <Input
          placeholder={I18n.t(
            'components.affiliate_three_sixty.affiliate.workspaces.search',
          )}
          preIcon={<Icon name="search" />}
          onChange={setSearchQuery}
          value={localSearchQuery}
        />
      </div>
      <div className="col-grid col-bleed-x direction-row justiy-space-between">
        <span className="col-grid col-bleed direction-row">
          <span className="affiliate-user-list__sort">
            <SortingPill
              size="small"
              parameterName={I18n.t(
                'components.affiliate_three_sixty.affiliate.workspaces.table.header.name',
              )}
              sortDirection={sortColumn === 'name' ? sortDirection : null}
              onClick={() => setSortWorkspace('name')}
            />
          </span>
          <span className="affiliate-user-list__sort">
            <SortingPill
              size="small"
              parameterName={I18n.t(
                'components.affiliate_three_sixty.affiliate.workspaces.table.header.identifier',
              )}
              sortDirection={sortColumn === 'identifier' ? sortDirection : null}
              onClick={() => setSortWorkspace('identifier')}
            />
          </span>
        </span>
        {isStateActive && (
          <span className="col-grid col-bleed direction-row justify-end">
            <Button
              label={
                <I18nText id="affiliate_three_sixty.affiliate.workspaces.new.button" />
              }
              version="v2"
              size="small"
              type="primary"
              onClick={() => {
                setModalOpen(true);
              }}
            />
          </span>
        )}
      </div>
      <WorkspaceTable
        workspaces={workspaces}
        isLoading={isLoading}
        setIsConfirmationModalOfUpdatingWorkspaceOpen={
          setIsConfirmationModalOfUpdatingWorkspaceOpen
        }
        fetchWorkspaces={fetchWorkspaces}
        deleteWorkspace={deleteWorkspace}
        status={status}
        userAffiliateId={userAffiliateId}
        affiliateId={id}
        workspaceName={workspaceName}
        setWorkspaceName={setWorkspaceName}
        workspaceIdentifier={workspaceIdentifier}
        setWorkspaceIdentifier={setWorkspaceIdentifier}
      />
      {!isLoading && (
        <div className="affiliate-list__pagination">
          <PaginationWithTranslation
            currentPage={page}
            totalPages={totalPages}
            onPaginationClick={value => setQuery({ page: value })}
          />
        </div>
      )}
      <ConfirmationModal
        confirmationHeader={
          <I18nText id="workspace.edit.confirmation_modal.success_header" />
        }
        subHeader={
          <I18nText id="workspace.create.confirmation_modal.success_subheader" />
        }
        onTimeout={() => setIsConfirmationModalOfCreatingWorkspaceOpen(false)}
        timeout={3000}
        withAction={false}
        type="success"
        isModalOpen={isConfirmationModalOfCreatingWorkspaceOpen}
        onConfirmationModalOpen={() =>
          setIsConfirmationModalOfCreatingWorkspaceOpen(true)
        }
        onConfirmationModalClose={() =>
          setIsConfirmationModalOfCreatingWorkspaceOpen(false)
        }
      />
      <ConfirmationModal
        confirmationHeader={
          <I18nText id="workspace.edit.confirmation_modal.success_header" />
        }
        subHeader={
          <I18nText id="workspace.edit.confirmation_modal.success_subheader" />
        }
        onTimeout={() => setIsConfirmationModalOfUpdatingWorkspaceOpen(false)}
        timeout={3000}
        withAction={false}
        type="success"
        isModalOpen={isConfirmationModalOfUpdatingWorkspaceOpen}
        onConfirmationModalOpen={() =>
          setIsConfirmationModalOfUpdatingWorkspaceOpen(true)
        }
        onConfirmationModalClose={() =>
          setIsConfirmationModalOfUpdatingWorkspaceOpen(false)
        }
      />
      <WorkSpaceModal
        onModalClose={() => setModalOpen(false)}
        isVisible={modalOpen}
        title={
          <I18nText id="affiliate_three_sixty.affiliate.workspaces.new.title" />
        }
        subheaderText={
          <I18nText id="affiliate_three_sixty.affiliate.workspaces.new.message" />
        }
        userAffiliateId={userAffiliateId}
        affiliates={affiliates.map(affiliate => ({
          value: affiliate.id,
          label: affiliate.clientName,
        }))}
        action={createWorkspace}
        fetchWorkspaces={fetchWorkspaces}
        openConfirmationModalOfCreatingWorkspace={
          openConfirmationModalOfCreatingWorkspace
        }
        isSuperAdmin={superAdmin}
        workspaceName={workspaceName}
        setWorkspaceName={setWorkspaceName}
        workspaceIdentifier={workspaceIdentifier}
        setWorkspaceIdentifier={setWorkspaceIdentifier}
        buttonText={<I18nText id="workspace.create.button_label.create" />}
      />
    </div>
  );
};

WorkspaceList.propTypes = {
  id: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  userAffiliateId: PropTypes.number.isRequired,
};

export default withQueryParamsProvider(WorkspaceList);
